<!--
  StorefrontBreadcrumb.vue -- P4 storefront chrome (breadcrumb trail).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P4 -- Shared chrome: CategoryNav + Breadcrumb (frontend).

  A pure, presentational, dependency-free breadcrumb. It renders a 1-to-3
  level trail:

    trail = [{ label, path? }, ...]

  Non-terminal entries (those carrying a `path`) render as router-links; the
  terminal entry (the last one, with no `path`) renders as plain text -- it is
  the current page. Callers build the paths using the Q7 canonical URL shape
  (/shop/<storeSlug>[/c/<slug>[/p/<id>]]) so the links resolve into the P5
  route tree.

  Structurally capped at 3 levels by construction (Home / Category / Product):
  the render walks at most the first 3 entries, so a longer trail can never
  produce a 4th crumb. This mirrors the design prototype's "never deeper than
  3 levels" invariant and Section 5's breadcrumb constraint.

  Test-id convention: `sf-breadcrumb` on the container; `sf-crumb-<index>` per
  crumb; `sf-breadcrumb-sep` on each between-crumb separator.
-->
<template>
  <nav class="sf-breadcrumb" data-testid="sf-breadcrumb" aria-label="Breadcrumb">
    <template v-for="(step, i) in cappedTrail" :key="i">
      <span
        v-if="i > 0"
        class="tf-icon sf-breadcrumb__sep"
        data-testid="sf-breadcrumb-sep"
        aria-hidden="true"
        >chevron_right</span
      >
      <router-link
        v-if="!isTerminal(i)"
        :to="step.path"
        class="sf-breadcrumb__link"
        :data-testid="`sf-crumb-${i}`"
        >{{ step.label }}</router-link
      >
      <span
        v-else
        class="sf-breadcrumb__current"
        :data-testid="`sf-crumb-${i}`"
        aria-current="page"
        >{{ step.label }}</span
      >
    </template>
  </nav>
</template>

<script>
// The breadcrumb is structurally capped at 3 levels (Home / Category /
// Product) -- Section 5 constraint + the design prototype's "never deeper
// than 3 levels" note. Enforced here so a caller passing a longer trail can
// never leak a 4th crumb.
const MAX_CRUMBS = 3;

export default {
  name: "StorefrontBreadcrumb",

  props: {
    // 1-to-3 entries: [{ label, path? }]. Non-terminal entries carry a `path`
    // and render as links; the terminal entry has no path and renders as
    // plain text (the current page).
    trail: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    // Never render more than MAX_CRUMBS -- the 4th-level guard by construction.
    cappedTrail() {
      return (Array.isArray(this.trail) ? this.trail : []).slice(0, MAX_CRUMBS);
    },
  },

  methods: {
    // The terminal crumb is the last one we actually render (after the cap),
    // OR any entry lacking a `path`. Rendering the current page as a link
    // would be wrong even if a stray path slipped onto the last entry, so the
    // last rendered crumb is always plain text.
    isTerminal(index) {
      if (index === this.cappedTrail.length - 1) return true;
      return !this.cappedTrail[index] || !this.cappedTrail[index].path;
    },
  },
};
</script>

<style scoped>
.sf-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 20px 32px 4px;
  font-size: 13px;
}

.sf-breadcrumb__link {
  color: var(--tf-primary, #119ae9);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.sf-breadcrumb__link:hover {
  text-decoration: underline;
}

.sf-breadcrumb__current {
  color: var(--tf-fg-1, #1b2a38);
  font-weight: 600;
}

/* The separator uses the shared `tf-icon` glyph class (as the rest of the
   storefront chrome does); this rule only owns size/color. data-testid marks
   it for tests; aria-hidden keeps it out of the accessibility tree. */
.sf-breadcrumb__sep {
  font-size: 16px;
  color: var(--tf-fg-5, #9aa6b0);
  user-select: none;
}
</style>
